/**
 * Sponsor CBO Performance
 * Compare and rank CBO performance across multiple metrics
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { apiClient } from '@/services/apiClient';
import { ArrowUpDown, TrendingUp, DollarSign } from 'lucide-react';
import ProgramSelector from '../shared/ProgramSelector';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface SponsorCBOPerformanceProps {
  sponsorId: number;
  initialProgramId?: number;
}

interface CBOPerformance {
  cbo_id: number;
  cbo_name: string;
  state_code: string;
  total_enrollments: number;
  active_enrollments: number;
  work_compliance_rate: number | null;
  avg_cost_per_enrollment: number;
  total_incentives_paid: number;
}

export const SponsorCBOPerformance: React.FC<SponsorCBOPerformanceProps> = ({
  sponsorId,
  initialProgramId
}) => {
  const { t } = useAppTranslation();
  const [cbos, setCbos] = useState<CBOPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'enrollments' | 'compliance_rate' | 'cost_per_enrollment'>('enrollments');
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(initialProgramId || null);

  useEffect(() => {
    fetchCBOPerformance();
  }, [sponsorId, sortBy, selectedProgramId]);

  const fetchCBOPerformance = async () => {
    try {
      setLoading(true);
      let url = `/api/sponsors/${sponsorId}/cbo-performance?sort_by=${sortBy}`;
      if (selectedProgramId) {
        url += `&program_id=${selectedProgramId}`;
      }
      const response = await apiClient.get(url);
      setCbos(response.data.cbos);
    } catch (err) {
      console.error('Error fetching CBO performance:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header with Program Selector */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h2 className="text-2xl font-bold">{t('sponsors.cbo_performance', 'CBO Performance')}</h2>
          <div className="flex items-center gap-4">
            <ProgramSelector
              selectedProgramId={selectedProgramId}
              onProgramChange={setSelectedProgramId}
              sponsorId={sponsorId}
              label={t('sponsors.program', 'Program')}
            />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="enrollments">{t('sponsors.sort_by_enrollments', 'Sort by Enrollments')}</option>
              <option value="compliance_rate">{t('sponsors.sort_by_compliance_rate', 'Sort by Compliance Rate')}</option>
              <option value="cost_per_enrollment">{t('sponsors.sort_by_cost', 'Sort by Cost')}</option>
            </select>
          </div>
        </div>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('sponsors.cbo_name', 'CBO Name')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('sponsors.state', 'State')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  {t('sponsors.enrollments', 'Enrollments')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  {t('sponsors.compliance_rate', 'Compliance Rate')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  {t('sponsors.costenrollment', 'Cost/Enrollment')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  {t('sponsors.total_paid', 'Total Paid')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
              {cbos.map((cbo) => (
                <tr key={cbo.cbo_id} className="hover:bg-gray-50 dark:bg-gray-700">
                  <td className="px-6 py-4">
                    <div className="font-medium">{cbo.cbo_name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">ID: {cbo.cbo_id}</div>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="outline">{cbo.state_code}</Badge>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="font-medium">{cbo.total_enrollments}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {cbo.active_enrollments} active
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {cbo.work_compliance_rate !== null ? (
                      <Badge
                        variant={cbo.work_compliance_rate >= 80 ? 'success' : 'warning'}
                      >
                        {cbo.work_compliance_rate.toFixed(1)}%
                      </Badge>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right font-medium">
                    ${cbo.avg_cost_per_enrollment.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-right font-medium text-green-600">
                    ${cbo.total_incentives_paid.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
